"use client";

import type { ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { toneClasses, toneIcon, type Tone } from "./badge";

type NoticeProps = {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  icon?: LucideIcon | null;
  actions?: ReactNode;
  className?: string;
  /** `alert` for errors/urgent, `status` for informational updates. */
  live?: "alert" | "status" | "off";
  compact?: boolean;
};

/** Inline callout. Uses colour + icon + text so it never relies on colour alone. */
export function Notice({ tone = "info", title, children, icon, actions, className, live, compact }: NoticeProps) {
  const Icon = icon === undefined ? toneIcon[tone] : icon;
  const role = live === "off" ? undefined : live ?? (tone === "danger" ? "alert" : "status");
  return (
    <div
      role={role}
      className={cn(
        "flex gap-3 rounded-xl",
        compact ? "px-3 py-2.5 text-sm" : "px-4 py-3.5 text-sm",
        toneClasses[tone],
        className,
      )}
    >
      {Icon ? <Icon className="mt-0.5 h-4.5 w-4.5 shrink-0" aria-hidden /> : null}
      <div className="min-w-0 flex-1 leading-relaxed">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={cn(title && "mt-0.5", "text-[0.92em] opacity-90")}>{children}</div> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2 self-center">{actions}</div> : null}
    </div>
  );
}

/** Notice that animates in/out when `message` changes to/from empty. */
export function InlineError({ message, className }: { message?: string | null; className?: string }) {
  return (
    <AnimatePresence initial={false}>
      {message ? (
        <motion.div
          key="err"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className={cn("overflow-hidden", className)}
        >
          <Notice tone="danger" compact>
            {message}
          </Notice>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

type EmptyProps = {
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  compact?: boolean;
};

export function EmptyState({ icon: Icon, title, description, action, className, compact }: EmptyProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-2xl border border-dashed border-line-strong text-center",
        compact ? "px-4 py-8" : "px-6 py-12",
        className,
      )}
    >
      {Icon ? (
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-soft text-brand-ink">
          <Icon className="h-6 w-6" aria-hidden />
        </span>
      ) : null}
      <p className={cn("font-display font-bold text-ink", Icon ? "mt-4" : "", compact ? "text-base" : "text-lg")}>{title}</p>
      {description ? <p className="mt-1.5 max-w-md text-sm leading-relaxed text-ink-2">{description}</p> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("skeleton", className)} aria-hidden />;
}

export function Spinner({ className, label }: { className?: string; label?: string }) {
  return (
    <span role="status" aria-live="polite" className={cn("inline-flex items-center gap-2 text-ink-2", className)}>
      <Loader2 className="h-5 w-5 animate-spin text-brand" aria-hidden />
      {label ? <span className="text-sm">{label}</span> : <span className="sr-only">…</span>}
    </span>
  );
}

/** Full-area centred loader for page-level pending states. */
export function PageLoader({ label }: { label?: string }) {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <Spinner label={label} />
    </div>
  );
}

type StatProps = {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  icon?: LucideIcon;
  className?: string;
};

export function Stat({ label, value, sub, tone, icon: Icon, className }: StatProps) {
  return (
    <div className={cn("rounded-2xl border border-line bg-surface p-4 shadow-1", className)}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-ink-2">{label}</p>
        {Icon ? (
          <span className={cn("flex h-7 w-7 items-center justify-center rounded-lg", tone ? toneClasses[tone] : "bg-surface-2 text-ink-2")}>
            <Icon className="h-4 w-4" aria-hidden />
          </span>
        ) : null}
      </div>
      <p className="tabular mt-2 font-display text-2xl font-bold text-ink">{value}</p>
      {sub ? <p className="mt-1 text-xs text-ink-2">{sub}</p> : null}
    </div>
  );
}
